
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UpdateMyProfileFormSchema, combineName, splitName, type Role } from '@shared/user';
import {
  Badge,
  Button,
  Card,
  Heading,
  Row,
  ROLE_BADGE_TONE,
  ROLE_LABEL,
  ROLE_ORDER,
  Stack,
  Text,
} from '@client/shared/design';
import { useForm, zodValidator, TextField } from '@client/shared/form/index.ts';
import { messageFor } from '@client/shared/lib/error-message.ts';
import { initials } from '@client/shared/lib/initials.ts';
import {
  updateMyProfile,
  VIEWER_QUERY_KEY,
  type Viewer,
} from './viewer-operations.ts';
import { SESSION_QUERY_KEY } from '@client/shared/auth/index.ts';
import styles from './MyAccount.module.css';

export interface IdentityBlockProps {
  name: string;
  email: string;
  roles: readonly Role[];
}

export function IdentityBlock({ name, email, roles }: IdentityBlockProps) {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: updateMyProfile,
    onSuccess: async (viewer: Viewer) => {
      queryClient.setQueryData(VIEWER_QUERY_KEY, viewer);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: VIEWER_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }),
      ]);
    },
  });

  const form = useForm({
    defaultValues: splitName(name),
    validators: { onSubmit: zodValidator(UpdateMyProfileFormSchema) },
    onSubmit: async ({ value }) => {
      setSubmitError(null);
      setSaved(false);
      try {
        const next = combineName(value.firstName, value.lastName);
        const viewer = await mutation.mutateAsync({ name: next });
        form.reset(splitName(viewer.name));
        setSaved(true);
      } catch (err) {
        setSubmitError(messageFor(err, 'Couldn’t save your name. Try again.'));
      }
    },
  });

  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 2600);
    return () => window.clearTimeout(t);
  }, [saved]);

  const sortedRoles = [...roles].sort(
    (a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b),
  );

  return (
    <Card pad="lg" spine as="section" aria-labelledby="identity-heading">
      <Stack gap={5}>
        <Row gap={4} className={styles.identityRow}>
          <span className={styles.avatar} aria-hidden="true">
            {initials(name)}
          </span>
          <Stack gap={1}>
            <Heading level={3} as="h2" id="identity-heading">
              Identity
            </Heading>
            <Text size="sm" tone="soft">
              Your display name labels every booking you own on the group calendar.
            </Text>
          </Stack>
        </Row>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit();
          }}
          noValidate
        >
          <Row gap={4} className={styles.nameRow}>
            <form.Field
              name="firstName"
              validators={{ onChange: zodValidator(UpdateMyProfileFormSchema.shape.firstName) }}
            >
              {(field) => (
                <TextField
                  field={field}
                  fieldClassName={styles.nameField}
                  label="First name"
                  autoComplete="given-name"
                  onValueChange={() => setSaved(false)}
                />
              )}
            </form.Field>

            <form.Field
              name="lastName"
              validators={{ onChange: zodValidator(UpdateMyProfileFormSchema.shape.lastName) }}
            >
              {(field) => (
                <TextField
                  field={field}
                  fieldClassName={styles.nameField}
                  label="Last name"
                  autoComplete="family-name"
                  onValueChange={() => setSaved(false)}
                />
              )}
            </form.Field>

            <div className={styles.nameActions}>
              {saved && (
                <span className={styles.savedFlag} role="status">
                  <CheckGlyph />
                  Saved
                </span>
              )}
              <form.Subscribe
                selector={(s) =>
                  [s.isSubmitting, s.values.firstName, s.values.lastName, s.isDirty] as const
                }
              >
                {([isSubmitting, first, last, isDirty]) => (
                  <Button
                    type="submit"
                    loading={Boolean(isSubmitting)}
                    disabled={
                      !isDirty ||
                      (first as string).trim().length === 0 ||
                      (last as string).trim().length === 0
                    }
                  >
                    Save name
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </Row>

          {submitError && (
            <Text size="sm" tone="danger" role="alert">
              {submitError}
            </Text>
          )}
        </form>

        <Stack gap={1}>
          <span className={styles.fieldLabel}>Email</span>
          <span className={styles.fieldValue}>{email}</span>
          <Text size="xs" tone="muted">
            This is your sign-in identity and can’t be changed here.
          </Text>
        </Stack>

        <Stack gap={1}>
          <span className={styles.fieldLabel}>Roles</span>
          <Row gap={2} wrap>
            {sortedRoles.length === 0 ? (
              <Text size="sm" tone="muted">
                No roles assigned.
              </Text>
            ) : (
              sortedRoles.map((role) => (
                <Badge key={role} tone={ROLE_BADGE_TONE[role]} dot>
                  {ROLE_LABEL[role]}
                </Badge>
              ))
            )}
          </Row>
          <Text size="xs" tone="muted">
            Managed by your group admin.
          </Text>
        </Stack>
      </Stack>
    </Card>
  );
}

function CheckGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12.5 10 17.5 19 6.5" />
    </svg>
  );
}
